/**
 * Undo/redo snapshot for in-place polygon edits of an OpeningAnnotation
 * (translate, rotate, align). Toggles between two polygon states (before /
 * after) on each `restore()` call.
 *
 * Sibling to `OpeningExistenceSnapshot` and `OpeningKindSnapshot` in
 * DoorWindowTool — same `Snapshot` interface, same toggle semantics.
 *
 * Persists changes to Apollo / GraphQL by dispatching `door_window_updated`
 * after writing the live measure's points, mirroring
 * `DoorWindowEditKindAdapter.commitEdit`. Lookup by identifier so the
 * snapshot still works when the live OpeningAnnotation instance has been
 * recreated (e.g. by a prior `OpeningExistenceSnapshot` round-trip).
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { Snapshot } from '../../ToolUndoRedoManager';
import { OpeningAnnotation } from '../../../../utils/PointoramaAnnotation';

/** Plain serializable position triple — frozen point of a polygon corner. */
interface PointPosition {
  x: number;
  y: number;
  z: number;
}

function freezePoints(points: THREE.Vector3[]): PointPosition[] {
  return points.map((p) => ({ x: p.x, y: p.y, z: p.z }));
}

export class OpeningGeometrySnapshot implements Snapshot {
  /** false = next restore applies `before` (undo); true = next restore applies `after` (redo). */
  private applyBefore = true;
  private readonly beforePoints: PointPosition[];
  private readonly afterPoints: PointPosition[];

  constructor(
    private readonly viewer: Viewer,
    private readonly identifier: string,
    before: THREE.Vector3[],
    after: THREE.Vector3[],
  ) {
    this.beforePoints = freezePoints(before);
    this.afterPoints = freezePoints(after);
  }

  restore(): Snapshot {
    const target = this.applyBefore ? this.beforePoints : this.afterPoints;
    const measure = this.viewer.scene.measurements.find((m) => m instanceof OpeningAnnotation && m.identifier === this.identifier) as OpeningAnnotation | undefined;
    if (measure) {
      // Rebuild Point[] with fresh Vector3s — `setPoints` clones internally
      // but it expects the input shape with `position` + `pointCloudId`,
      // and we want the snapshot to be immune to live-annotation mutations.
      const newPoints = target.map((p) => ({ position: new THREE.Vector3(p.x, p.y, p.z), pointCloudId: '' }));
      measure.setPoints(newPoints);
      this.viewer.scene.dispatchEvent({
        type: 'door_window_updated',
        annotationId: this.identifier,
        polygon: target.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      });
    }
    this.applyBefore = !this.applyBefore;
    return this;
  }
}
